import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { throwIfEmpty, timeout } from 'rxjs';
import { SeatWorkQueue, WorkQueueResponse } from '../models/work-queue.model';
import {
  QueueSummary,
  SeatRow,
  buildSeatRows,
  summarizeQueue,
} from '../models/work-queue.utils';

export const BRIDGE_BASE = 'https://fn-ryn-bridge-upsllc.azurewebsites.net';

// 30s autorefresh. The disagreement lens (active-but-silent, report-past-death)
// keys off freshness signals with a 15-minute liveness window, so a half-minute
// of staleness is harmless while keeping the pane close to live. The
// BridgeAuthInterceptor (app.config) attaches the Bearer JWT to this call.
export const WORK_QUEUE_REFRESH_MS = 30_000;

/**
 * Work-queue pane data (WI 30957). Fetches GET /api/ryn-db/work-queue (the
 * bridge's per-seat self-report + derived arms) and exposes classified,
 * sorted display rows and the headline summary. All display logic is the pure
 * work-queue.utils; this service is transport + signal state only, mirroring
 * the claude-quota / overflow-queue sibling panes.
 */
@Injectable({ providedIn: 'root' })
export class WorkQueueService {
  private readonly http = inject(HttpClient);

  private readonly seatsSig = signal<SeatWorkQueue[]>([]);
  private readonly loadingSig = signal(false);
  private readonly errorSig = signal<string | null>(null);
  private readonly lastFetchedAtSig = signal<Date | null>(null);
  private readonly showRetiredSig = signal(false);

  readonly loading = this.loadingSig.asReadonly();
  readonly error = this.errorSig.asReadonly();
  readonly lastFetchedAt = this.lastFetchedAtSig.asReadonly();
  /** Show-retired toggle (WI 31108 follow-up). The bridge hides soft-deleted
   * seats by default; when on, refresh() opts in via ?showRetired=true and the
   * pane renders them greyed. */
  readonly showRetired = this.showRetiredSig.asReadonly();

  setShowRetired(on: boolean): void {
    if (this.showRetiredSig() === on) return;
    this.showRetiredSig.set(on);
    // Refetch immediately: toggling OFF must also drop already-loaded retired
    // rows, and the server is the filter authority, not a client-side hide.
    this.refresh();
  }

  // lastFetchedAt participates so ages re-derive against a fresh "now" on every
  // refresh, not only when the seat payload changes.
  readonly rows = computed<SeatRow[]>(() => {
    const at = this.lastFetchedAtSig();
    return buildSeatRows(this.seatsSig(), at ? at.getTime() : Date.now());
  });

  readonly summary = computed<QueueSummary>(() => summarizeQueue(this.rows()));

  /** Monotonic request stamp: only the NEWEST refresh may write state. Without
   * it, last-write-wins let a stale showRetired=true response land after the
   * toggle turned off and resurrect retired rows into the default view (grok
   * residual 3, PR 2786). */
  private requestSeq = 0;

  refresh(): void {
    const seq = ++this.requestSeq;
    this.loadingSig.set(true);
    this.errorSig.set(null);
    // The bridge is the filter authority for retired rows (?showRetired=true
    // opts in, WI 31108); the pane never client-side-hides what it fetched.
    const qs = this.showRetiredSig() ? '?showRetired=true' : '';
    this.http
      .get<WorkQueueResponse>(`${BRIDGE_BASE}/api/ryn-db/work-queue${qs}`)
      .pipe(
        timeout({ first: 20_000 }),
        throwIfEmpty(
          () =>
            new Error(
              'Bridge authentication did not return work queue data. Retry after signing in.'
            )
        )
      )
      .subscribe({
        next: (resp) => {
          if (seq !== this.requestSeq) return;
          this.seatsSig.set(resp?.seats ?? []);
          this.lastFetchedAtSig.set(new Date());
          this.loadingSig.set(false);
        },
        error: (err) => {
          if (seq !== this.requestSeq) return;
          this.errorSig.set(this.formatError(err));
          this.loadingSig.set(false);
        },
      });
  }

  private formatError(err: unknown): string {
    const e = err as { status?: number; message?: string; error?: unknown };
    if (e?.status === 401)
      return 'Bridge auth required to read the work queue (GET /api/ryn-db/work-queue).';
    if (e?.status === 0) return 'Network error reaching the bridge.';
    // The endpoint returns { error: "<message>" }; older bridge routes use
    // { message: "..." }. Read a raw string body too, then fall back.
    const body = e?.error;
    if (typeof body === 'string') return body;
    if (body && typeof body === 'object') {
      const b = body as { error?: string; message?: string };
      if (typeof b.error === 'string') return b.error;
      if (typeof b.message === 'string') return b.message;
    }
    return e?.message ?? 'Unknown error';
  }
}
